import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export enum ZipCodeFormat {
  ZIP_5 = 'ZIP_5',
  ZIP_6_SPACED = 'ZIP_6_SPACED',
  ZIP_9 = 'ZIP_9',
}

export interface ZipCodeConfig {
  format: ZipCodeFormat;
  countryCode?: string;
}

const COUNTRY_FORMAT_MAP: Record<string, ZipCodeFormat> = {
  US: ZipCodeFormat.ZIP_6_SPACED,
  CA: ZipCodeFormat.ZIP_6_SPACED,
};

const INVALID_CHAR_PATTERN = /[^A-Za-z0-9\s]/;
const ALPHANUMERIC_PATTERN = /[^A-Za-z0-9]/g;

@Injectable({ providedIn: 'root' })
export class ZipConfigService {
  private config: ZipCodeConfig = {
    format: ZipCodeFormat.ZIP_6_SPACED,
    countryCode: 'US',
  };

  getConfig(): ZipCodeConfig {
    return { ...this.config };
  }

  getFormat(): ZipCodeFormat {
    return this.config.format;
  }

  setFormat(format: ZipCodeFormat, countryCode?: string): void {
    this.config = {
      format,
      countryCode: countryCode ?? this.config.countryCode,
    };
  }

  setFormatByCountry(countryCode: string): void {
    const format = COUNTRY_FORMAT_MAP[countryCode.toUpperCase()] ?? ZipCodeFormat.ZIP_6_SPACED;
    this.config = { format, countryCode: countryCode.toUpperCase() };
  }

  getMaxLength(): number {
    return this.getMaxLengthForFormat(this.config.format);
  }

  getZipValidators(): ValidatorFn[] {
    return [this.createValidator(this.getConfig())];
  }

  extractZipValue(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'object' && value !== null && 'zip_code' in value) {
      return String((value as { zip_code: string }).zip_code ?? '').trim();
    }
    return String(value).trim();
  }

  getAlphanumericOnly(value: string): string {
    return value.replace(ALPHANUMERIC_PATTERN, '');
  }

  normalizeZipValue(value: string, format: ZipCodeFormat = this.config.format): string {
    return this.formatZipInput(value, format);
  }
  formatZipInput(value: string, format: ZipCodeFormat = this.config.format): string {
    const sanitized = this.sanitizeZipInput(value, format);

    switch (format) {
      case ZipCodeFormat.ZIP_5:
        return this.getAlphanumericOnly(sanitized).slice(0, 5);

      case ZipCodeFormat.ZIP_9:
        return this.getAlphanumericOnly(sanitized).slice(0, 9);

      case ZipCodeFormat.ZIP_6_SPACED: {
        const alnum = this.getAlphanumericOnly(sanitized);
        if (alnum.length > 6) {
          return alnum.slice(0, 9);
        }
        if (alnum.length === 6) {
          return `${alnum.slice(0, 3)} ${alnum.slice(3)}`;
        }
        return alnum;
      }

      default:
        return sanitized;
    }
  }

  private createValidator(config: ZipCodeConfig): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = this.extractZipValue(control.value);

      if (!raw) {
        return null;
      }

      const normalized = this.normalizeZipValue(raw, config.format);

      if (this.isValidZipValue(normalized, config.format)) {
        return null;
      }

      return { invalidZip: true };
    };
  }

  private sanitizeZipInput(value: string, format: ZipCodeFormat): string {
    if (format === ZipCodeFormat.ZIP_6_SPACED) {
      return value.replace(/[^A-Za-z0-9\s]/g, '');
    }
    return value.replace(ALPHANUMERIC_PATTERN, '');
  }

  private hasInvalidZipChars(value: string): boolean {
    return INVALID_CHAR_PATTERN.test(value);
  }

  private getMaxLengthForFormat(format: ZipCodeFormat): number {
    switch (format) {
      case ZipCodeFormat.ZIP_5:
        return 5;
      case ZipCodeFormat.ZIP_6_SPACED:
      case ZipCodeFormat.ZIP_9:
        return 9;
      default:
        return 9;
    }
  }

  private isValidZipValue(value: string, format: ZipCodeFormat): boolean {
    if (!value) {
      return true;
    }

    if (this.hasInvalidZipChars(value)) {
      return false;
    }

    const alnum = this.getAlphanumericOnly(value);

    switch (format) {
      case ZipCodeFormat.ZIP_5:
        return alnum.length === 5 && /^[A-Za-z0-9]{5}$/.test(alnum);

      case ZipCodeFormat.ZIP_9:
        return alnum.length === 9 && !value.includes(' ') && /^[A-Za-z0-9]{9}$/.test(alnum);

      case ZipCodeFormat.ZIP_6_SPACED: {
        const len = alnum.length;
        if (len === 7 || len === 8) {
          return false;
        }
        if (len === 5) {
          return !value.includes(' ') && /^[A-Za-z0-9]{5}$/.test(alnum);
        }
        if (len === 6) {
          if (value.includes(' ') && !/^[A-Za-z0-9]{3}\s[A-Za-z0-9]{3}$/.test(value.trim())) {
            return false;
          }
          return /^[A-Za-z0-9]{6}$/.test(alnum);
        }
        if (len === 9) {
          return !value.includes(' ') && /^[A-Za-z0-9]{9}$/.test(alnum);
        }
        return false;
      }

      default:
        return false;
    }
  }
}
